import {
  useGetLessonPlayback,
  getGetLessonPlaybackQueryKey,
} from "@workspace/api-client-react";
import { Play, Lock, AlertTriangle } from "lucide-react";
import { useUser } from "@clerk/react";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";

type Props = {
  lessonId: number;
  hasVideo: boolean;
  durationMinutes: number | null;
  courseSlug: string;
};

/**
 * Renders an async lesson's video. The lesson detail payload only tells us
 * whether a video exists (`hasVideo`) — the actual GCS object key is private.
 * To play, we hit `GET /lessons/:id/playback` which checks enrollment server-
 * side and returns a short-lived signed GET URL we plug into a `<video>`.
 */
export function LessonVideoPlayer({
  lessonId,
  hasVideo,
  durationMinutes,
  courseSlug,
}: Props) {
  const { isSignedIn, isLoaded } = useUser();

  const { data, isLoading, error } = useGetLessonPlayback(lessonId, {
    query: {
      enabled: hasVideo && isLoaded && !!isSignedIn,
      queryKey: getGetLessonPlaybackQueryKey(lessonId),
      // Signed URLs are valid for 2h; refetch a bit before they expire so a
      // student who pauses the tab and comes back doesn't hit a 403.
      staleTime: 60 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: false,
    },
  });

  if (!hasVideo) {
    return (
      <div
        className="bg-muted aspect-video rounded-2xl flex flex-col items-center justify-center text-muted-foreground border border-dashed"
        role="status"
        aria-label="Video not yet uploaded"
      >
        <Play className="w-10 h-10 mb-2 opacity-40" aria-hidden="true" />
        <div className="font-medium">Video coming soon</div>
        <div className="text-xs mt-1">
          Your teacher hasn&apos;t uploaded this lesson yet.
        </div>
      </div>
    );
  }

  if (isLoaded && !isSignedIn) {
    return (
      <div className="bg-card border rounded-2xl p-8 md:p-12 text-center shadow-sm flex flex-col items-center justify-center aspect-video">
        <div className="w-16 h-16 bg-primary/10 text-primary rounded-full flex items-center justify-center mb-4">
          <Lock className="w-8 h-8" />
        </div>
        <h3 className="text-xl font-bold mb-2">Sign in to watch</h3>
        <p className="text-muted-foreground mb-6 max-w-sm">
          This lesson is for enrolled students. Sign in with the email you used
          to enroll.
        </p>
        <Button asChild>
          <Link href="/sign-in">Sign in</Link>
        </Button>
      </div>
    );
  }

  if (isLoading || (!isLoaded && hasVideo)) {
    return (
      <div className="bg-black aspect-video rounded-2xl flex items-center justify-center text-white/60">
        Loading video…
      </div>
    );
  }

  if (error) {
    const status = (error as { status?: number; response?: { status?: number } } | null)
      ?.status ??
      (error as { response?: { status?: number } } | null)?.response?.status;
    const isForbidden = status === 403;
    return (
      <div className="bg-card border rounded-2xl p-8 md:p-12 text-center shadow-sm flex flex-col items-center justify-center aspect-video">
        <div className="w-16 h-16 bg-destructive/10 text-destructive rounded-full flex items-center justify-center mb-4">
          {isForbidden ? <Lock className="w-8 h-8" /> : <AlertTriangle className="w-8 h-8" />}
        </div>
        <h3 className="text-xl font-bold mb-2">
          {isForbidden ? "Enrolled students only" : "Couldn't load video"}
        </h3>
        <p className="text-muted-foreground mb-6 max-w-sm">
          {isForbidden
            ? "Enroll in this course to watch the lesson videos."
            : "Refresh the page or try again in a moment."}
        </p>
        {isForbidden && (
          <Button asChild>
            <Link href={`/courses/${courseSlug}`}>View course</Link>
          </Button>
        )}
      </div>
    );
  }

  if (!data?.url) {
    return null;
  }

  return (
    <div className="bg-black aspect-video rounded-2xl overflow-hidden shadow-xl">
      <video
        key={data.url}
        src={data.url}
        controls
        controlsList="nodownload"
        playsInline
        preload="metadata"
        className="w-full h-full"
        aria-label={`Lesson video${durationMinutes ? `, ${durationMinutes} minutes` : ""}`}
      />
    </div>
  );
}
